// src/components/Documents/SimplePDFViewer.js (Updated with proper toggle)
import React, { useState, useRef, useEffect } from 'react';
import { 
  Download, 
  ExternalLink, 
  ZoomIn, 
  ZoomOut, 
  RefreshCw,
  AlertCircle,
  Eye,
  FileText,
  Sidebar,
  List,
  Grid,
  Link as LinkIcon,
  Settings
} from 'lucide-react';
import InteractivePDFViewer from './InteractivePDFViewer';

const SimplePDFViewer = ({ document }) => {
  const [viewerType, setViewerType] = useState('native');
  const [scale, setScale] = useState(100);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSidebar, setShowSidebar] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [interceptLinks, setInterceptLinks] = useState(true); // Default to true for better UX
  const iframeRef = useRef(null);
  const objectRef = useRef(null);

  // Auto-switch to interactive viewer when link interception is enabled
  useEffect(() => {
    if (interceptLinks && viewerType === 'native') {
      setViewerType('interactive');
    }
  }, [interceptLinks, viewerType]);

  const downloadDocument = () => {
    const link = document.createElement('a');
    link.href = document.url;
    link.download = document.name;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openInNewTab = () => {
    window.open(document.url, '_blank', 'noopener,noreferrer');
  };

  const handleLoad = () => {
    setIsLoading(false);
    setError(null);
  };

  const handleError = () => {
    setIsLoading(false);
    setError('PDF viewer failed to load');
  };

  const buildPdfUrl = () => {
    const baseUrl = document.url;
    const params = new URLSearchParams();
    
    params.set('zoom', scale.toString());
    params.set('page', '1');
    params.set('view', 'FitH');
    params.set('navpanes', showSidebar ? '1' : '0');
    params.set('toolbar', '1');
    params.set('scrollbar', '1');
    
    if (viewMode === 'list') {
      params.set('pagemode', 'none');
    } else {
      params.set('pagemode', 'thumbs');
    }
    
    return `${baseUrl}#${params.toString()}`;
  };

  const retryLoad = () => {
    setError(null);
    setIsLoading(true);
    if (iframeRef.current) {
      const newUrl = buildPdfUrl();
      iframeRef.current.src = '';
      setTimeout(() => {
        iframeRef.current.src = newUrl;
      }, 100);
    }
  };

  const ViewerControls = () => (
    <div className="bg-gray-50 border-b px-4 py-3 flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Mode:</span>
          <select
            value={viewerType}
            onChange={(e) => setViewerType(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1"
          >
            <option value="native">Browser Viewer</option>
            <option value="interactive">Interactive Viewer</option>
            <option value="object">Object Embed</option>
            <option value="download">Download Only</option>
          </select>
        </div>

        {/* Link Interception Toggle */}
        <div className="flex items-center space-x-2">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={interceptLinks}
              onChange={(e) => setInterceptLinks(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <LinkIcon className="w-4 h-4 text-gray-600" />
            <span className="text-sm text-gray-700">Intercept Links</span>
          </label>
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        {viewerType === 'native' && (
          <>
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className={`p-2 rounded transition-colors ${
                showSidebar 
                  ? 'bg-indigo-100 text-indigo-600' 
                  : 'hover:bg-gray-200 text-gray-600'
              }`}
              title={showSidebar ? "Hide Sidebar" : "Show Sidebar"}
            >
              <Sidebar className="w-4 h-4" />
            </button>

            <button
              onClick={() => setViewMode(viewMode === 'list' ? 'thumbnails' : 'list')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'list' 
                  ? 'bg-indigo-100 text-indigo-600' 
                  : 'hover:bg-gray-200 text-gray-600'
              }`}
              title={`Switch to ${viewMode === 'list' ? 'Thumbnails' : 'List'} View`}
            >
              {viewMode === 'list' ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
            </button>

            <button
              onClick={() => {
                setScale(Math.max(25, scale - 25));
                retryLoad();
              }}
              className="p-2 hover:bg-gray-200 rounded transition-colors"
              title="Zoom Out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-600 min-w-16 text-center">
              {scale}%
            </span>
            <button
              onClick={() => {
                setScale(Math.min(500, scale + 25));
                retryLoad();
              }}
              className="p-2 hover:bg-gray-200 rounded transition-colors"
              title="Zoom In"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </>
        )}
        
        <button
          onClick={retryLoad}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
        <button
          onClick={openInNewTab}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Open in New Tab"
        >
          <ExternalLink className="w-4 h-4" />
        </button>
        <button
          onClick={downloadDocument}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Download"
        >
          <Download className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  const renderViewer = () => {
    if (viewerType === 'interactive') {
      return (
        <InteractivePDFViewer 
          document={document} 
          interceptLinks={interceptLinks}
        />
      );
    }

    // ... rest of the viewer implementations remain the same
    if (viewerType === 'download') {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="text-center max-w-md">
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">PDF Ready for Download</h3>
            <p className="text-gray-600 mb-6">
              Download the PDF to view it in your preferred PDF application.
            </p>
            <div className="space-y-3">
              <button
                onClick={downloadDocument}
                className="w-full bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
              >
                <Download className="w-5 h-5 mr-2" />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Native iframe viewer
    const iframeUrl = buildPdfUrl();
    
    return (
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Loading PDF...</p>
            </div>
          </div>
        )}
        
        <iframe
          ref={iframeRef}
          src={iframeUrl}
          title={`PDF Viewer - ${document.name}`}
          className="w-full h-full border-0"
          style={{ minHeight: '700px' }}
          onLoad={handleLoad}
          onError={handleError}
          allow="fullscreen"
          loading="lazy"
        />
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
      <ViewerControls />
      
      {/* Status Bar */}
      {viewerType === 'interactive' && !error && (
        <div className="bg-green-50 border-b border-green-200 px-4 py-2">
          <div className="flex items-center justify-between text-sm text-green-800">
            <div className="flex items-center space-x-2">
              <LinkIcon className="w-4 h-4" />
              <span>
                Interactive Mode: {interceptLinks ? 'Links open in modal' : 'Standard link behavior'}
              </span>
            </div>
            <span className="text-xs text-green-600">
              Click on any link to see the interception in action
            </span>
          </div>
        </div>
      )}

      <div className="relative bg-gray-100" style={{ minHeight: '700px' }}>
        {renderViewer()}
      </div>
    </div>
  );
};

export default SimplePDFViewer;